import type { BlockDefinition, VariableMeta } from '@tsagko/label-contracts'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { formatMm } from '../canvas/format-mm'
import { computeZoneLayout, type LaidOutZone } from '../canvas/layout'
import { collectRegions, type LeafRegionInfo } from './region-derivation'
import { mergeVariables } from './variables-ops'

interface RegionTableProps {
  block: BlockDefinition
  selectedZoneId?: string
  onSelect: (zone: LaidOutZone) => void
}

function formatSummary(meta: VariableMeta | undefined): string {
  if (!meta?.format || meta.format.kind === 'plain') return ''
  if (meta.format.kind === 'date') return meta.format.pattern
  if (meta.format.kind === 'number') {
    const parts: string[] = []
    if (meta.format.decimals !== undefined) parts.push(`${meta.format.decimals} dp`)
    if (meta.format.suffix) parts.push(`+${meta.format.suffix}`)
    return parts.join(' ')
  }
  return ''
}

function alignmentLabel(p?: { hAlign: string; vAlign: string }): string {
  if (!p) return '—'
  return `${p.vAlign.charAt(0).toUpperCase()}/${p.hAlign.charAt(0).toUpperCase()}`
}

function typeIcon(p: LeafRegionInfo['primitive']): string {
  if (p.type === 'text' || p.type === 'localized-text' || p.type === 'localized-rich-text') return 'T'
  if (p.type === 'symbol') return '▮▮▮'
  if (p.type === 'image') return '🖼'
  if (p.type === 'separator') return '─'
  return '?'
}

function sourceCellLabel(region: LeafRegionInfo): string {
  const p = region.primitive
  if (p.type === 'text') return p.value.content.slice(0, 40)
  if (p.type === 'symbol') {
    const ds = p.dataStructure
    if (ds.kind === 'raw' || ds.kind === 'gtin' || ds.kind === 'url') {
      return ds.value.content.slice(0, 40)
    }
    return ds.kind
  }
  return p.type
}

export function RegionTable({ block, selectedZoneId, onSelect }: RegionTableProps) {
  const { t } = useTranslation()
  const regions = useMemo(
    () => collectRegions(block.layout, { widthMm: block.widthMm, heightMm: block.heightMm }),
    [block.layout, block.widthMm, block.heightMm]
  )
  const variables = useMemo(
    () => mergeVariables(block.layout, block.variables ?? {}),
    [block.layout, block.variables]
  )
  const laidOut = useMemo(
    () => computeZoneLayout(block.layout, { widthMm: block.widthMm, heightMm: block.heightMm }),
    [block.layout, block.widthMm, block.heightMm]
  )

  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})

  useEffect(() => {
    if (!selectedZoneId) return
    const row = rowRefs.current[selectedZoneId]
    // jsdom doesn't implement scrollIntoView; guard so tests don't crash.
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedZoneId])

  return (
    <div data-testid="region-table" className="border-b border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <table className="w-full text-xs">
        <thead className="bg-stone-50 text-stone-500 dark:bg-stone-950">
          <tr>
            <th className="px-2 py-1 text-left">{t('blockConfigurator.regionTable.type')}</th>
            <th className="px-2 py-1 text-left">{t('blockConfigurator.regionTable.source')}</th>
            <th className="px-2 py-1 text-left">{t('blockConfigurator.regionTable.default')}</th>
            <th className="px-2 py-1 text-left">{t('blockConfigurator.regionTable.format')}</th>
            <th className="px-2 py-1 text-left">{t('blockConfigurator.regionTable.align')}</th>
            <th className="px-2 py-1 text-left">{t('blockConfigurator.regionTable.size')}</th>
          </tr>
        </thead>
        <tbody>
          {regions.map(region => {
            const variableMeta = region.variableName ? variables[region.variableName] : undefined
            const selected = region.zoneId === selectedZoneId
            return (
              <tr
                key={region.zoneId}
                ref={el => {
                  rowRefs.current[region.zoneId] = el
                }}
                data-testid="region-row"
                data-zone-id={region.zoneId}
                onClick={() => {
                  const laid = laidOut.find(z => z.id === region.zoneId)
                  if (laid) onSelect(laid)
                }}
                className={`cursor-pointer ${
                  selected
                    ? 'bg-sky-100 dark:bg-sky-900'
                    : 'hover:bg-stone-50 dark:hover:bg-stone-950'
                }`}
              >
                <td className="px-2 py-1 font-mono">{typeIcon(region.primitive)}</td>
                <td className="px-2 py-1 font-mono">{sourceCellLabel(region)}</td>
                <td className="px-2 py-1">{variableMeta?.default ?? ''}</td>
                <td className="px-2 py-1">{formatSummary(variableMeta)}</td>
                <td className="px-2 py-1">{alignmentLabel(region.placement)}</td>
                {/* #126: toFixed(0) rendered a 69 × 7.5 region as "69 × 8".
                    formatMm keeps the real value at 1 dp. Separator stays ` × `. */}
                <td className="px-2 py-1">{formatMm(region.widthMm)} × {formatMm(region.heightMm)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
